/*
 * Copyright 2025 coze-dev Authors
 * Modifications Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type FC, useRef } from 'react';

import {
  type IChatUploadCopywritingConfig,
  DEFAULT_MAX_FILE_SIZE,
  UploadType,
} from '@adp-common/chat-uikit-shared';
import {
  FILE_TYPE_CONFIG,
  FileTypeEnum,
} from '@adp-common/chat-core/shared/const';
import { Toast, Upload } from '@coze-arch/coze-design';

interface IChatUploadProps {
  /**
   * Upload event callback
   * @param uploadType [IMAGE = 0 FILE = 1]
   * @param file
   * @returns void
   */
  onUpload: (uploadType: UploadType, file: File) => void;
  /**
   * Copywriting information configuration
   */
  copywritingConfig?: IChatUploadCopywritingConfig;
  /**
   * Maximum file size (in bytes)
   */
  maxFileSize?: number;
  isDisabled?: boolean;
  children: JSX.Element;
  limitFileCount?: number;
  isFileCountExceedsLimit: (fileCount: number) => boolean;
}

const findFileTypeConfig = (file: File) =>
  FILE_TYPE_CONFIG.find(
    cnf => cnf.judge?.(file) || cnf.accept.some(ext => file.name.endsWith(ext)),
  );

const getFileFingerprint = (file: File) =>
  [file.name, file.size, file.lastModified, file.type].join(':');

export const ChatUpload: FC<IChatUploadProps> = props => {
  const {
    copywritingConfig = {},
    maxFileSize = DEFAULT_MAX_FILE_SIZE,
    children,
    onUpload,
    isDisabled,
    isFileCountExceedsLimit,
    limitFileCount = 1,
  } = props;
  const pendingSelectionKeysRef = useRef(new Set<string>());

  /**
   * handle uploads
   * @param fileList
   * @returns void
   */
  const handleUpload = (fileList: File[]) => {
    const { fileSizeReachLimitToast, fileExceedsLimitToast, fileEmptyToast } =
      copywritingConfig;

    if (!fileList.length) {
      return;
    }

    const seenFingerprints = new Set<string>();
    const uniqueFiles = fileList.filter(file => {
      const fingerprint = getFileFingerprint(file);
      if (seenFingerprints.has(fingerprint)) {
        return false;
      }
      seenFingerprints.add(fingerprint);
      return true;
    });

    const selectionKey = uniqueFiles.map(getFileFingerprint).sort().join('|');

    if (pendingSelectionKeysRef.current.has(selectionKey)) {
      return;
    }

    pendingSelectionKeysRef.current.add(selectionKey);
    setTimeout(() => {
      pendingSelectionKeysRef.current.delete(selectionKey);
    }, 0);

    if (isFileCountExceedsLimit(uniqueFiles.length)) {
      Toast.warning({
        showClose: false,
        content: fileExceedsLimitToast,
      });
      return;
    }

    // Is there a file that is out of size?
    const hasOverflowLimitFileSize = uniqueFiles.some(
      file => file.size > maxFileSize,
    );
    const hasEmptyFile = uniqueFiles.some(file => file.size <= 0);

    // Error handling if the file size exceeds the expected size
    if (hasOverflowLimitFileSize) {
      Toast.warning({
        showClose: false,
        content: fileSizeReachLimitToast,
      });
    }

    if (hasEmptyFile) {
      Toast.warning({
        showClose: false,
        content: fileEmptyToast,
      });
    }

    const verifiedFileTypeConfigList = uniqueFiles
      .filter(file => file.size <= maxFileSize && file.size > 0)
      .map(file => ({
        file,
        fileTypeConfig: findFileTypeConfig(file),
      }));

    for (const fileConfig of verifiedFileTypeConfigList) {
      if (fileConfig.fileTypeConfig?.fileType === FileTypeEnum.IMAGE) {
        onUpload?.(UploadType.IMAGE, fileConfig.file);
      } else {
        onUpload?.(UploadType.FILE, fileConfig.file);
      }
    }
  };

  return (
    <Upload
      limit={limitFileCount === 1 ? 1 : undefined}
      draggable={false}
      action=""
      fileList={[]}
      onFileChange={handleUpload}
      disabled={isDisabled}
      multiple={limitFileCount > 1}
      uploadTrigger={'custom'}
    >
      {children}
    </Upload>
  );
};

ChatUpload.displayName = 'UiKitChatUpload';
